import React from "react";
import { Box, Typography, Grid, Button } from "@mui/material";

// Import all the necessary icons
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SubjectIcon from "@mui/icons-material/Subject";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { Link as LinkIcon } from "@mui/icons-material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import EditIcon from "@mui/icons-material/Edit";
import WikipediaIcon from "./WikipediaIcon";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DescriptionIcon from "@mui/icons-material/Description";
/**
 *
 * ThemeSelectorButtons Component
 * Displays a grid of option buttons for users to select their content source
 */
const ThemeSelectorButtons = ({
  theme,
  styles,
  forTextlabbet = false,
  forTeachers,
  user,
  onOptionSelect,
}) => {
  const isDark = theme.palette.mode === "dark";
  // Extract common button styling into a reusable object
  const buttonStyle = {
    p: 3,
    pt: 10,
    pb: 10,
    display: "flex",
    flexDirection: "column",
    height: "140px",
    borderRadius: 4,
    backgroundColor: isDark ? "#1c262f" : "#f1f5f942 !important",
    transition:
      "background-color 0.3s ease, transform 0.3s ease, border 0.3s ease",
    //border: "1px solid transparent",
    "& .MuiSvgIcon-root": {
      transform: "scale(1)",
      transition: "transform 0.3s ease",
    },
    "&:hover": {
      backgroundColor: isDark ? "#1c262f" : "#f1effe4f",
      border: `1px solid ${theme.palette.primary.main}`,
      transform: "scale(1.02)",
      "& .MuiSvgIcon-root": {
        transform: "scale(1.05)",
      },
      "& .MuiTypography-root": {
        color: "text.primary",
        fontWeight: 600,
      },
    },
    "&": {
      "& > svg": {},
    },
    "& .MuiTypography-root": {
      fontWeight: "500",
      fontSize: "0.98rem",
      textTransform: "none",
      mt: 0.9,
      transition: "color 0.3s ease",
    },
  };

  // Common icon styling
  const iconStyle = {
    fontSize: "3.2rem",
    mb: 1,
    color: "#7c60f4",
    backgroundColor: "#7c60f41a",
    borderRadius: 100,
    padding: "10px",
  };

  // Image styling for logos
  const logoStyle = {
    width: 40,
    height: 40,
    objectFit: "contain",
    mb: 1,
  };

  return (
    <Box
      sx={{
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 4,
          textAlign: "center",
          color: "text.secondary",
          fontSize: "1.2rem",
          display: "none",
        }}
      >
        Välj ett alternativ nedan för att komma igång
      </Typography>

      <Grid container spacing={3} justifyContent="center">
        {!forTeachers && (
          <Grid item xs={12} sm={6} md={user.accountType === "teacher" ? 3 : 4}>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                ...styles.freshBox,
                ...buttonStyle,
              }}
              onClick={() => onOptionSelect("customTheme")}
            >
              <EditIcon sx={iconStyle} />
              <Typography>
                {forTextlabbet ? "Ange din text" : "Egna instruktioner"}
              </Typography>
            </Button>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={user.accountType === "teacher" ? 3 : 4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{
              ...styles.freshBox,
              ...buttonStyle,
            }}
            onClick={() => onOptionSelect("fileUpload")}
          >
            <FileUploadIcon sx={iconStyle} />
            <Typography>Ladda upp eget material</Typography>
          </Button>
        </Grid>

        {/* Paste Text */}
        <Grid item xs={12} sm={6} md={user.accountType === "teacher" ? 3 : 4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{
              ...styles.freshBox,
              ...buttonStyle,
            }}
            onClick={() => onOptionSelect("pasteText")}
          >
            <SubjectIcon sx={iconStyle} />
            <Typography>Klistra in text</Typography>
          </Button>
        </Grid>
        {/* Custom Theme / Text Input */}

        {/* File Upload */}

        {/* Show these options only when not in textlabbet mode */}
        {!forTextlabbet && (
          <>
            {!forTeachers && (
              <Grid
                item
                xs={12}
                sm={6}
                md={user.accountType === "teacher" ? 3 : 4}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    ...styles.freshBox,
                    ...buttonStyle,
                  }}
                  onClick={() => onOptionSelect("youtube")}
                >
                  <YouTubeIcon
                    sx={{ ...iconStyle, color: "#ff0033", display: "none" }}
                  />
                  <YouTubeIcon sx={{ ...iconStyle }} />
                  <Typography>Video från YouTube</Typography>
                </Button>
              </Grid>
            )}
            {/* Image Upload */}
            <Grid
              item
              xs={12}
              sm={6}
              md={user.accountType === "teacher" ? 3 : 4}
            >
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  ...styles.freshBox,
                  ...buttonStyle,
                }}
                onClick={() => onOptionSelect("vision")}
              >
                <AddPhotoAlternateIcon sx={iconStyle} />
                <Typography>Ladda upp bild</Typography>
              </Button>
            </Grid>
            {/* URL Input */}
            {!forTeachers && (
              <Grid
                item
                xs={12}
                sm={6}
                md={user.accountType === "teacher" ? 3 : 4}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    ...styles.freshBox,
                    ...buttonStyle,
                  }}
                  onClick={() => onOptionSelect("url")}
                >
                  <WikipediaIcon sx={iconStyle} />
                  <Typography>Artikel från Wikipedia</Typography>
                </Button>
              </Grid>
            )}

            {/* Wikipedia */}
            {user.accountType === "student" && (
              <Grid item xs={12} sm={6} md={4} sx={{ display: "none" }}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    ...styles.freshBox,
                    ...buttonStyle,
                  }}
                  onClick={() => onOptionSelect("wikipedia")}
                >
                  <WikipediaIcon sx={iconStyle} />
                  <Typography>Wikipedia-artikel</Typography>
                </Button>
              </Grid>
            )}
            {/* SO-rummet */}
            {user.accountType === "teacher" && (
              <Grid item xs={12} sm={6} md={3} sx={{}}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    ...styles.freshBox,
                    ...buttonStyle,
                  }}
                  onClick={() => onOptionSelect("so-rummet")}
                >
                  {/*
                  <Box
                    component="img"
                    src="/img/so-logga.png"
                    alt="SO-rummet"
                    sx={logoStyle}
                  />
                  */}
                  <LibraryBooksIcon sx={iconStyle} />
                  <Typography>Artikel från SO-rummet</Typography>
                </Button>
              </Grid>
            )}
            {!forTeachers && (
              <>
                {user.accountType !== "student" && (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={user.accountType === "teacher" ? 3 : 4}
                  >
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        ...styles.freshBox,
                        ...buttonStyle,
                      }}
                      onClick={() => onOptionSelect("subject")}
                    >
                      {/*
                      <Box
                        component="img"
                        src="/img/skolverkets.jpg"
                        alt="Skolverket"
                        sx={logoStyle}
                      />
                      */}
                      <DescriptionIcon sx={iconStyle} />
                      <Typography>Skolverkets Kursplan</Typography>
                    </Button>
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};

export default ThemeSelectorButtons;
